import { useRef, useState } from "react";
import { uploadServicePhoto, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { Camera, Upload, Loader2, ImageIcon } from "lucide-react";
import ServicePhotoViewer from "@/components/service/ServicePhotoViewer";
import CameraCancelHint from "@/components/CameraCancelHint";

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function ServicePhotos({ photos, onChange }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(null); // {done, total}
  const [viewIdx, setViewIdx] = useState(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    let next = [...photos];
    for (let i = 0; i < files.length; i++) {
      setBusy({ done: i + 1, total: files.length });
      try {
        const { path } = await uploadServicePhoto(files[i]);
        next = [...next, { id: newId(), path, note: "" }];
        await onChange(next);
      } catch (err) {
        toast.error(errorMessage(err, `Photo ${i + 1} failed`));
      }
    }
    setBusy(null);
  };

  return (
    <div className="space-y-2" data-testid="service-photos">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">
          Teardown photos <span className="font-mono-data">({photos.length})</span>
        </div>
        <div className="flex gap-1.5">
          <PickBtn onClick={() => cameraRef.current?.click()} disabled={!!busy} testid="service-photos-camera"><Camera size={11} /> Camera</PickBtn>
          <PickBtn onClick={() => galleryRef.current?.click()} disabled={!!busy} testid="service-photos-gallery"><Upload size={11} /> Gallery</PickBtn>
        </div>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} data-testid="service-photos-camera-input" />
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="service-photos-gallery-input" />
      </div>
      <CameraCancelHint testid="service-photos-camera-hint" />

      {busy && (
        <div className="flex items-center gap-2 text-[11px] text-[#3A2A10]" data-testid="service-photos-progress">
          <Loader2 size={12} className="animate-spin text-[#D4AF37]" /> Uploading {busy.done} of {busy.total}…
        </div>
      )}

      {photos.length === 0 && !busy ? (
        <div className="border border-dashed border-[#8B6A1F]/40 py-6 text-center text-[11px] text-[#3A2A10]" data-testid="service-photos-empty">
          <ImageIcon size={18} className="mx-auto mb-1.5 opacity-60" strokeWidth={1.3} />
          Snap each stage of the teardown so you can rebuild it in reverse.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 items-start">
          {photos.map((p, i) => (
            <PhotoTile key={p.id} photo={p} n={i + 1} onOpen={() => setViewIdx(i)} />
          ))}
        </div>
      )}

      <ServicePhotoViewer
        photos={photos}
        index={viewIdx}
        onIndex={setViewIdx}
        onChange={onChange}
      />
    </div>
  );
}

function PickBtn({ children, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 px-2 h-7 text-[10px] uppercase tracking-wider border border-[#8B6A1F]/45 rounded-sm text-[#0A0604] hover:bg-[#2B2214]/[0.06] disabled:opacity-50 transition-colors"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function PhotoTile({ photo, n, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative text-left flex flex-col justify-start"
      data-testid={`service-photo-tile-${n}`}
    >
      <div className="aspect-square overflow-hidden rounded-sm border border-[#8B6A1F]/35 bg-[#2B2214]/[0.06] group-hover:border-[#D4AF37] transition-colors">
        <img src={fileUrl(photo.path, 320)} alt={`Photo ${n}`} loading="lazy" className="w-full h-full object-cover" />
      </div>
      <span className="absolute top-1 left-1 min-w-[20px] h-5 px-1 flex items-center justify-center text-[10px] font-mono-data bg-[#0A0604]/75 text-[#F9F0D4] rounded-sm">
        {n}
      </span>
      {photo.note && (
        <div className="mt-1 text-[10px] leading-tight text-[#3A2A10] line-clamp-2 break-words">{photo.note}</div>
      )}
    </button>
  );
}
